import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Inbox,
  Info,
  RotateCw,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../cn.js';
import { Button } from './primitives.js';

// ------------------------------------------------------------------ Modal / BottomSheet

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** On phones, present as a bottom sheet (default). */
  sheet?: boolean;
}

/**
 * Native <dialog>: focus trap, Escape and inert background come from the platform.
 * Centered glass modal from 640px up; a bottom sheet with a grabber on phones.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  sheet = true,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      data-sheet={sheet}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className={cn(
        'ui-dialog glass-thick m-auto w-[calc(100%-2rem)] rounded-panel p-0 text-fg',
        size === 'sm' ? 'max-w-md' : size === 'lg' ? 'max-w-3xl' : 'max-w-xl',
      )}
    >
      {open && (
        <div className="flex max-h-[inherit] flex-col overflow-hidden rounded-[inherit]">
          {sheet && (
            <div className="flex justify-center pt-2 sm:hidden" aria-hidden>
              <span className="h-1.5 w-10 rounded-full bg-[var(--ui-line-strong)]" />
            </div>
          )}
          <header className="flex items-center justify-between gap-4 px-5 pt-4 pb-3 sm:px-6 sm:pt-5">
            <h2 className="min-w-0 text-lg font-semibold">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="pressable focus-ring tap-target grid size-9 shrink-0 place-items-center rounded-full bg-[var(--control-bg)] text-muted hover:bg-[var(--control-bg-hover)] hover:text-fg"
              aria-label={t('common.close')}
            >
              <X className="size-4" />
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-1 pb-[max(1.25rem,var(--safe-bottom))] sm:px-6 sm:pb-6">
            {children}
          </div>
          {footer && (
            <footer className="ui-dialog-footer flex flex-col-reverse gap-2 border-t border-line px-5 py-3.5 sm:flex-row sm:justify-end sm:px-6 max-sm:*:w-full">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}

/** Same as Modal, named for intent: always a sheet on phones. */
export function BottomSheet(props: Omit<ModalProps, 'sheet'>) {
  return <Modal {...props} sheet />;
}

// ------------------------------------------------------------------ Toasts

type ToastTone = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}
interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const toastIcon: Record<ToastTone, LucideIcon> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const region = useRef<HTMLDivElement>(null);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++;
      setToasts((list) => [...list.slice(-3), { id, tone, message }]);
      setTimeout(() => dismiss(id), tone === 'error' ? 7000 : 4000);
    },
    [dismiss],
  );

  // The region is a manual popover so toasts render above an open modal dialog. Re-showing it
  // moves it to the top of the top layer when a dialog opened after it.
  useLayoutEffect(() => {
    const el = region.current;
    if (!el || typeof el.showPopover !== 'function') return;
    try {
      if (el.matches(':popover-open')) el.hidePopover();
      if (toasts.length > 0) el.showPopover();
    } catch {
      // Popover unsupported or already in the requested state.
    }
  }, [toasts]);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        ref={region}
        popover="manual"
        aria-live="polite"
        className="ui-toasts pointer-events-none flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => {
          const Icon = toastIcon[toast.tone];
          return (
            <div
              key={toast.id}
              role={toast.tone === 'error' ? 'alert' : 'status'}
              className="glass-thick pointer-events-auto relative flex w-full max-w-md animate-toast items-start gap-3 rounded-2xl py-3 ps-4 pe-2 text-sm"
            >
              <Icon
                className={cn(
                  'mt-0.5 size-5 shrink-0',
                  toast.tone === 'success'
                    ? 'text-success'
                    : toast.tone === 'error'
                      ? 'text-danger'
                      : 'text-brand',
                )}
                aria-hidden
              />
              <span className="min-w-0 flex-1 py-0.5 font-medium">{toast.message}</span>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="pressable focus-ring grid size-7 shrink-0 place-items-center rounded-full text-muted hover:bg-[var(--control-bg-hover)] hover:text-fg"
                aria-label={t('common.close')}
              >
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// ------------------------------------------------------------------ Confirm

export interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(
    null,
  );

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setState({ ...opts, resolve })),
    [],
  );
  const settle = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={state !== null}
        onClose={() => settle(false)}
        size="sm"
        title={state?.title ?? t('common.areYouSure')}
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={state?.danger ? 'danger' : 'primary'}
              onClick={() => settle(true)}
              autoFocus
            >
              {state?.confirmLabel ?? t('common.confirm')}
            </Button>
          </>
        }
      >
        <div className="flex gap-3 text-sm leading-relaxed">
          {state?.danger && (
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-danger-soft text-danger">
              <AlertTriangle className="size-4.5" aria-hidden />
            </span>
          )}
          <div className="min-w-0 self-center">{state?.message}</div>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

// ------------------------------------------------------------------ States

function StateIcon({ icon: Icon, tone }: { icon: LucideIcon; tone: 'neutral' | 'danger' }) {
  return (
    <span
      className={cn(
        'relative grid size-14 place-items-center rounded-2xl glass-rim',
        tone === 'danger'
          ? 'bg-danger-soft text-danger'
          : 'bg-[var(--control-bg)] text-muted shadow-[inset_0_0_0_1px_var(--ui-line)]',
      )}
    >
      <Icon className="size-6" aria-hidden />
    </span>
  );
}

export function EmptyState({
  message,
  action,
  title,
  icon = Inbox,
}: {
  message: ReactNode;
  action?: ReactNode;
  title?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex animate-fade flex-col items-center gap-3 px-4 py-12 text-center">
      <StateIcon icon={icon} tone="neutral" />
      {title && <p className="mt-1 text-base font-semibold">{title}</p>}
      <p className="max-w-sm text-sm text-muted">{message}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: ReactNode; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex animate-fade flex-col items-center gap-3 px-4 py-12 text-center"
    >
      <StateIcon icon={AlertOctagon} tone="danger" />
      <p className="max-w-sm text-sm">{message}</p>
      {onRetry && (
        <Button
          variant="secondary"
          size="sm"
          icon={<RotateCw className="size-4" />}
          onClick={onRetry}
          className="mt-1"
        >
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

const alertTone = {
  warning: { cls: 'bg-warning-soft text-warning', icon: AlertTriangle },
  danger: { cls: 'bg-danger-soft text-danger', icon: AlertOctagon },
  info: { cls: 'bg-brand-soft text-brand', icon: Info },
  success: { cls: 'bg-success-soft text-success', icon: CheckCircle2 },
} as const;

export function Alert({
  tone = 'warning',
  children,
}: {
  tone?: keyof typeof alertTone;
  children: ReactNode;
}) {
  const { cls, icon: Icon } = alertTone[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'note'}
      className={cn(
        'flex animate-fade gap-3 rounded-lg px-4 py-3.5 text-sm leading-relaxed shadow-[inset_0_0_0_1px_color-mix(in_srgb,currentColor_14%,transparent)]',
        cls,
      )}
    >
      <Icon className="mt-0.5 size-4.5 shrink-0" aria-hidden />
      <div className="min-w-0 [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2">
        {children}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Sticky action bar

/** Floating glass bar for unsaved-changes actions; sits above the phone bottom nav. */
export function StickyActionBar({
  message,
  children,
}: {
  message?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className="glass-thick sticky z-[var(--z-sticky)] flex animate-rise flex-wrap items-center justify-end gap-2 rounded-panel p-2.5 ps-4"
      style={{ bottom: 'var(--bottom-chrome)' }}
    >
      {message && (
        <span className="me-auto flex min-w-0 items-center gap-2 py-1 text-sm text-muted">
          <span className="size-2 shrink-0 rounded-full bg-warning" aria-hidden />
          {message}
        </span>
      )}
      <div className="flex gap-2 max-sm:w-full max-sm:*:flex-1">{children}</div>
    </div>
  );
}
